// Feature flags for the application
export enum DefaultFeatureFlags {
  USE_NEUMORPHISM = 'USE_NEUMORPHISM',
  ENABLE_DARK_MODE = 'ENABLE_DARK_MODE',
  NOTIFICATIONS = 'NOTIFICATIONS',
  ADVANCED_SECURITY = 'ADVANCED_SECURITY',
  USER_PROFILES = 'USER_PROFILES',
  USER_ROLES = 'USER_ROLES',
  GENERAL_SETTINGS = 'GENERAL_SETTINGS',
  SECURITY_SETTINGS = 'SECURITY_SETTINGS',
  REPORTS = 'REPORTS',
}

// Default state of feature flags
const defaultFeatureFlags: Record<DefaultFeatureFlags, boolean> = {
  [DefaultFeatureFlags.USE_NEUMORPHISM]: true,
  [DefaultFeatureFlags.ENABLE_DARK_MODE]: true,
  [DefaultFeatureFlags.NOTIFICATIONS]: true,
  [DefaultFeatureFlags.ADVANCED_SECURITY]: false,
  [DefaultFeatureFlags.USER_PROFILES]: true,
  [DefaultFeatureFlags.USER_ROLES]: true,
  [DefaultFeatureFlags.GENERAL_SETTINGS]: true,
  [DefaultFeatureFlags.SECURITY_SETTINGS]: true,
  [DefaultFeatureFlags.REPORTS]: false,
};

// Current state of feature flags (initialized with defaults)
let currentFeatureFlags = { ...defaultFeatureFlags };

/**
 * Check if a feature is enabled
 * @param flag The feature flag to check
 * @returns True if the feature is enabled, false otherwise
 */
export function isFeatureEnabled(flag: DefaultFeatureFlags): boolean {
  return currentFeatureFlags[flag] ?? false;
}

/**
 * Enable a feature flag
 * @param flag The feature flag to enable
 */
export function enableFeature(flag: DefaultFeatureFlags): void {
  currentFeatureFlags[flag] = true;
}

/**
 * Disable a feature flag
 * @param flag The feature flag to disable
 */
export function disableFeature(flag: DefaultFeatureFlags): void {
  currentFeatureFlags[flag] = false;
}

/**
 * Toggle a feature flag
 * @param flag The feature flag to toggle
 * @returns The new state of the feature flag
 */
export function toggleFeature(flag: DefaultFeatureFlags): boolean {
  currentFeatureFlags[flag] = !currentFeatureFlags[flag];
  return currentFeatureFlags[flag];
}

/**
 * Reset feature flags to their default state
 */
export function resetFeatureFlags(): void {
  currentFeatureFlags = { ...defaultFeatureFlags };
}

/**
 * Get all feature flags and their current state
 * @returns Record of all feature flags and their current state
 */
export function getAllFeatureFlags(): Record<DefaultFeatureFlags, boolean> {
  return { ...currentFeatureFlags };
}

/**
 * Update multiple feature flags at once
 * @param flags Record of feature flags to update and their new state
 */
export function updateFeatureFlags(flags: Partial<Record<DefaultFeatureFlags, boolean>>): void {
  currentFeatureFlags = { ...currentFeatureFlags, ...flags };
} 